import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { isAdminOrSuperAdmin } from '../utils/auth';
import '../App.css';
import LoadingSpinner from '../components/LoadingSpinner';

interface ChangeLog {
  id: string;
  userId: string;
  changedBy: string;
  field: 'phoneNumber' | 'address' | 'email' | 'soccerExperience' | 'password';
  oldValue?: string;
  newValue?: string;
  changedAt: string;
  userName?: string;
  userEmail?: string;
  changedByName?: string;
}

const UserChangeLogs: React.FC = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState<ChangeLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterUserId, setFilterUserId] = useState<string>('');

  useEffect(() => {
    if (isAdminOrSuperAdmin(user?.profileType)) {
      fetchLogs();
    }
  }, [user, filterUserId]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const endpoint = filterUserId 
        ? `/users/${filterUserId}/change-logs`
        : '/users/change-logs/all';
      const response = await api.get(endpoint);
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching change logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFieldLabel = (field: string) => {
    const labels: Record<string, string> = {
      phoneNumber: 'Phone Number',
      address: 'Address',
      email: 'Email',
      soccerExperience: 'Soccer Experience',
      password: 'Password'
    };
    return labels[field] || field;
  };

  if (!isAdminOrSuperAdmin(user?.profileType)) {
    return (
      <div className="container">
        <div className="card">
          <h1>Access Denied</h1>
          <p>Only administrators can view change logs.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <h1>User Change Logs</h1>
        <p style={{ color: '#757575', marginBottom: '20px' }}>
          View all changes made to user profiles. Only administrators can access this page.
        </p>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
            Filter by User ID (leave empty for all users):
          </label>
          <input
            type="text"
            value={filterUserId}
            onChange={(e) => setFilterUserId(e.target.value)}
            placeholder="user-1"
            style={{ padding: '8px', width: '300px', marginRight: '10px' }}
          />
          <button className="btn btn-primary" onClick={fetchLogs}>
            Apply Filter
          </button>
          {filterUserId && (
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                setFilterUserId('');
                fetchLogs();
              }}
              style={{ marginLeft: '10px' }}
            >
              Clear Filter
            </button>
          )}
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : logs.length === 0 ? (
          <p>No change logs found.</p>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Date/Time</th>
                  <th>User</th>
                  <th>User Email</th>
                  <th>Field Changed</th>
                  <th>Old Value</th>
                  <th>New Value</th>
                  <th>Changed By</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td>{new Date(log.changedAt).toLocaleString()}</td>
                    <td>{log.userName || log.userId}</td>
                    <td>{log.userEmail || '-'}</td>
                    <td>
                      <span className="badge badge-info">{getFieldLabel(log.field)}</span>
                    </td>
                    <td style={{ maxWidth: '200px', wordBreak: 'break-word' }}>
                      {log.field === 'password' ? '[REDACTED]' : (log.oldValue || '-')}
                    </td>
                    <td style={{ maxWidth: '200px', wordBreak: 'break-word' }}>
                      {log.field === 'password' ? '[REDACTED]' : (log.newValue || '-')}
                    </td>
                    <td>{log.changedByName || log.changedBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserChangeLogs;


